import { RespondentType } from "@models/RespondentType";
import { createContext, Dispatch, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { FCX } from "@datassential/platform-ui-lib";
import { AmongPayload, useGetAmongPayload } from "@vms/SelectAmong/useGetAmongPayload";
import { useAmongCopyTableText } from "@api/useAmongCopyTableText";

export type SelectAmongData = {
    respondentTypes: RespondentType[];
    restaurants: number[];
    cStores: number[];
    supermarkets: number[];
    hotels: number[];
    brands: number[];
    segmentsAndOnSites: number[];
    deliveryServices: number[];
}

type SelectAmongContextType = {
    data: SelectAmongData;
    setPartial: Dispatch<Partial<SelectAmongData>>;
    amongPayload: AmongPayload;
    amongCopyTableText: string | null;
}

export const DEFAULT_SELECT_AMONG_DATA: SelectAmongData = {
    respondentTypes: [],
    restaurants: [],
    cStores: [],
    supermarkets: [],
    hotels: [],
    brands: [],
    segmentsAndOnSites: [],
    deliveryServices: [],
}

const SelectAmongContext = createContext<SelectAmongContextType>({
    data: DEFAULT_SELECT_AMONG_DATA,
    setPartial: () => {},
    amongPayload: {},
    amongCopyTableText: null,
});

const contextValueReducer = (
    state: SelectAmongData,
    change: Partial<SelectAmongData>,
): SelectAmongData => {
    return {
        ...DEFAULT_SELECT_AMONG_DATA,
        ...state,
        ...change,
    };
};

export function useSelectAmongData(
    initialData: SelectAmongData = DEFAULT_SELECT_AMONG_DATA
): [
    SelectAmongData,
    Dispatch<Partial<SelectAmongData>>
] {
    return useReducer(contextValueReducer, initialData);
}

export const SelectAmongContextProvider: FCX<{
    initialData?: Partial<SelectAmongData>;
}> = ({
    initialData,
    children,
}) => {
    const [data, dispatchPartialData] = useSelectAmongData();
    const [isInitialDataSet, setIsInitialDataSet] = useState(false);
    const getPayload = useGetAmongPayload();
    const payload = useMemo(
        () => getPayload(data),
        [data, getPayload],
    );

    const { data: text } = useAmongCopyTableText(payload);

    const contextValue = useMemo(
        () => ({
            data,
            setPartial: dispatchPartialData,
            amongPayload: payload,
            amongCopyTableText: text,
        }),
        [data, dispatchPartialData, payload, text]
    );

    useEffect(() => {
        if (initialData && !isInitialDataSet) {
            setIsInitialDataSet(true);
            dispatchPartialData(initialData);
        }
    }, [initialData, dispatchPartialData, isInitialDataSet]);

    return (
        <SelectAmongContext.Provider value={contextValue}>
            {children}
        </SelectAmongContext.Provider>
    );
}

export function useSelectAmongContext(): SelectAmongContextType {
    const context = useContext(SelectAmongContext);
    if (!context) throw new Error("SelectAmong context is not initialized");
    return context;
}